import React, { useState, useEffect, useCallback, useRef } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Chip,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  IconButton,
  Tooltip,
} from '@mui/material'
import Alert from '@mui/material/Alert'
import Autocomplete from '@mui/material/Autocomplete'
import {
  PlayArrow as PlayIcon,
  Visibility as PreviewIcon,
  Refresh as RefreshIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  GetApp as DownloadIcon,
  Save as SaveIcon,
  History as HistoryIcon,
  Stop as StopIcon,
} from '@mui/icons-material'
import { authenticatedFetch, resolveAccessToken } from '@services/api/apiClient'
import { apiEndpoints } from '../../../consts'
import { LoadingSpinner, StatusChip } from '@components/atoms'
import { BaseTable } from '@components/TableComponents'
import { useFetch } from '@hooks'
import sharedStyles from '@views/settings/qualityAssurance/SettingsQA.module.scss'
import styles from './AutoQA.module.scss'

export default function SettingsAutoQA() {
  const [loading, setLoading] = useState(false)
  const [dryRunResult, setDryRunResult] = useState(null)
  const [runDialog, setRunDialog] = useState(false)
  const [runDate, setRunDate] = useState('')
  const [runLimit, setRunLimit] = useState('')
  const [runningBatch, setRunningBatch] = useState(false)
  const [batchResult, setBatchResult] = useState(null)
  const [expandedBatch, setExpandedBatch] = useState(null)
  const [batchDetails, setBatchDetails] = useState({})
  const [streamLogs, setStreamLogs] = useState([])
  const [streamSessionId, setStreamSessionId] = useState(null)
  const [streamProgress, setStreamProgress] = useState(null)
  const eventSourceRef = useRef(null)
  const retryTimeoutRef = useRef(null)
  const streamEpochRef = useRef(0)
  const maxRetries = 3

  const [allCampaigns, setAllCampaigns] = useState([])
  const [priorityCampaigns, setPriorityCampaigns] = useState([])
  const [maxHandleTime, setMaxHandleTime] = useState(600)
  const [configLoading, setConfigLoading] = useState(false)
  const [savingConfig, setSavingConfig] = useState(false)
  const [configSaved, setConfigSaved] = useState(false)
  const [configHasChanges, setConfigHasChanges] = useState(false)

  const { data: status, refetch: refetchStatus } = useFetch(apiEndpoints.autoQaStatus, {
    transform: (data) => data.data,
    errorMessage: 'Error fetching status',
  })

  const fetchSelectionConfig = useCallback(async () => {
    setConfigLoading(true)
    try {
      const response = await authenticatedFetch(apiEndpoints.autoQaSelectionConfig)
      const data = await response.json()
      const config = data.data
      setPriorityCampaigns(config.priorityCampaigns || [])
      setMaxHandleTime(config.maxHandleTime || 600)
      setConfigHasChanges(false)
    } catch (error) {
      console.error('Error fetching selection config:', error)
    } finally {
      setConfigLoading(false)
    }
  }, [])

  const fetchAllCampaigns = useCallback(async () => {
    try {
      const response = await authenticatedFetch(apiEndpoints.autoQaCampaigns)
      const data = await response.json()
      setAllCampaigns(data.data || [])
    } catch (error) {
      console.error('Error fetching campaigns:', error)
    }
  }, [])

  const handleSaveConfig = async () => {
    setSavingConfig(true)
    setConfigSaved(false)
    try {
      const response = await authenticatedFetch(apiEndpoints.autoQaSelectionConfig, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ priorityCampaigns, maxHandleTime }),
      })
      await response.json()
      setConfigHasChanges(false)
      setConfigSaved(true)
      setTimeout(() => setConfigSaved(false), 3000)
    } catch (error) {
      console.error('Error saving selection config:', error)
    } finally {
      setSavingConfig(false)
    }
  }

  useEffect(() => {
    fetchSelectionConfig()
    fetchAllCampaigns()
  }, [fetchSelectionConfig, fetchAllCampaigns])

  useEffect(() => {
    return () => {
      streamEpochRef.current += 1
      clearTimeout(retryTimeoutRef.current)
      if (eventSourceRef.current) {
        eventSourceRef.current.close()
        eventSourceRef.current = null
      }
    }
  }, [])

  const { data: history, refetch: refetchHistory } = useFetch(
    `${apiEndpoints.autoQaBatchHistory}?limit=20`,
    {
      initialData: [],
      transform: (data) => data.data?.runs || [],
      errorMessage: 'Error fetching history',
    },
  )

  const runDryRun = async () => {
    setLoading(true)
    setDryRunResult(null)
    try {
      const body = {}
      if (runDate) body.date = runDate
      if (runLimit) body.limit = parseInt(runLimit, 10)

      const response = await authenticatedFetch(apiEndpoints.autoQaBatchDryRun, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      })
      const data = await response.json()
      setDryRunResult(data.data)
    } catch (error) {
      console.error('Error running dry run:', error)
    } finally {
      setLoading(false)
    }
  }

  const startBatchRun = async (currentRetry = 0) => {
    setRunningBatch(true)
    setBatchResult(null)
    setRunDialog(false)
    if (currentRetry === 0) {
      setStreamLogs([])
      setStreamProgress(null)
    }

    const params = new URLSearchParams()
    if (runDate) params.append('date', runDate)
    if (runLimit) params.append('limit', runLimit)
    const apiKey = process.env.REACT_APP_API_KEY
    if (apiKey) params.append('apiKey', apiKey)
    const streamEpoch = streamEpochRef.current
    const accessToken = await resolveAccessToken()
    if (streamEpoch !== streamEpochRef.current) return
    if (accessToken) params.append('access_token', accessToken)

    const url = `${apiEndpoints.autoQaBatchStream}?${params.toString()}`
    const eventSource = new EventSource(url)
    eventSourceRef.current = eventSource

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data)

      if (data.type === 'connected') {
        setStreamSessionId(data.sessionId)
        setStreamLogs((prev) => [
          ...prev,
          { type: 'info', message: data.message, timestamp: new Date() },
        ])
      } else if (data.type === 'started' || data.type === 'batch_created') {
        setStreamLogs((prev) => [
          ...prev,
          { type: 'info', message: data.message, timestamp: new Date() },
        ])
      } else if (data.type === 'progress') {
        setStreamProgress(data)
        if (data.qaGrade) {
          setStreamLogs((prev) => [
            ...prev,
            {
              type:
                data.qaGrade === 'PASS' ? 'success' : data.qaGrade === 'FAIL' ? 'warning' : 'info',
              message: data.message,
              timestamp: new Date(),
            },
          ])
        }
      } else if (data.type === 'complete' || data.type === 'cancelled') {
        setBatchResult(data)
        setRunningBatch(false)
        setStreamSessionId(null)
        eventSource.close()
        refetchHistory()
      } else if (data.type === 'error') {
        setStreamLogs((prev) => [
          ...prev,
          { type: 'error', message: data.error || data.message, timestamp: new Date() },
        ])
        setBatchResult({ success: false, message: data.error || data.message })
        setRunningBatch(false)
        setStreamSessionId(null)
        eventSource.close()
      }
    }

    eventSource.onerror = () => {
      eventSource.close()
      setStreamSessionId(null)

      if (currentRetry < maxRetries) {
        const nextRetry = currentRetry + 1
        const delay = Math.pow(2, currentRetry) * 1000
        setStreamLogs((prev) => [
          ...prev,
          {
            type: 'warning',
            message: `Connection lost. Retrying in ${delay / 1000}s... (attempt ${nextRetry}/${maxRetries})`,
            timestamp: new Date(),
          },
        ])
        retryTimeoutRef.current = setTimeout(() => startBatchRun(nextRetry), delay)
      } else {
        setStreamLogs((prev) => [
          ...prev,
          {
            type: 'error',
            message: `Connection lost after ${maxRetries} retry attempts`,
            timestamp: new Date(),
          },
        ])
        setRunningBatch(false)
        setBatchResult({ success: false, message: 'Connection lost - please try again' })
      }
    }
  }

  const stopBatchRun = async () => {
    if (!streamSessionId) return
    try {
      await authenticatedFetch(apiEndpoints.autoQaBatchStop, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sessionId: streamSessionId }),
      })
      setStreamLogs((prev) => [
        ...prev,
        { type: 'warning', message: 'Stop requested...', timestamp: new Date() },
      ])
    } catch (error) {
      console.error('Error stopping batch:', error)
    }
  }

  const cancelBatch = async (batchId) => {
    if (!window.confirm(`Cancel Auto QA batch #${batchId}? Any in-flight calls will be skipped.`))
      return
    try {
      const res = await authenticatedFetch(apiEndpoints.autoQaBatchCancel(batchId), {
        method: 'POST',
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}))
        throw new Error(body.error || `Failed with status ${res.status}`)
      }
      refetchHistory()
    } catch (error) {
      console.error('Error cancelling batch:', error)
      window.alert(`Failed to cancel: ${error.message}`)
    }
  }

  const fetchBatchDetails = async (batchId) => {
    if (batchDetails[batchId]) {
      setExpandedBatch(expandedBatch === batchId ? null : batchId)
      return
    }

    try {
      const response = await authenticatedFetch(
        `${apiEndpoints.autoQaBatchResults(batchId)}?limit=50`,
      )
      const data = await response.json()
      setBatchDetails((prev) => ({ ...prev, [batchId]: data.data.results }))
      setExpandedBatch(batchId)
    } catch (error) {
      console.error('Error fetching batch details:', error)
    }
  }

  const downloadReport = async (batchId) => {
    try {
      const response = await authenticatedFetch(apiEndpoints.autoQaBatchReport(batchId))
      const data = await response.json()

      if (data.data?.downloadUrl) {
        const link = document.createElement('a')
        link.href = data.data.downloadUrl
        link.download = data.data.fileName || `qa-batch-report-${batchId}.txt`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      }
    } catch (error) {
      console.error('Error downloading report:', error)
    }
  }

  const getStatusChipColor = (s) => {
    switch (s) {
      case 'completed':
        return 'success'
      case 'running':
        return 'primary'
      case 'failed':
        return 'error'
      case 'pending':
        return 'warning'
      default:
        return 'neutral'
    }
  }

  const getStatusIcon = (s) => {
    switch (s) {
      case 'completed':
        return <SuccessIcon style={{ color: 'var(--color-success)', fontSize: 18 }} />
      case 'running':
        return <CircularProgress size={16} />
      case 'failed':
        return <ErrorIcon style={{ color: 'var(--color-error)', fontSize: 18 }} />
      case 'pending':
        return <PendingIcon style={{ color: 'var(--color-warning)', fontSize: 18 }} />
      default:
        return null
    }
  }

  const formatDuration = (start, end) => {
    if (!start || !end) return '-'
    const ms = new Date(end) - new Date(start)
    const seconds = Math.floor(ms / 1000)
    const minutes = Math.floor(seconds / 60)
    if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`
    }
    return `${seconds}s`
  }

  const dryRunColumns = [
    { id: 'callId', label: 'Call ID' },
    { id: 'campaignName', label: 'Campaign' },
    { id: 'agentName', label: 'Agent' },
    {
      id: 'duration',
      label: 'Duration',
      renderCell: (row) => row.durationFormatted || `${Math.round(row.duration / 60)}m`,
    },
    { id: 'disposition', label: 'Disposition' },
  ]

  const batchHistoryColumns = [
    {
      id: 'id',
      label: 'ID',
      renderCell: (row) => (
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {row.id}
        </Typography>
      ),
    },
    {
      id: 'batch_date',
      label: 'Date',
      renderCell: (row) => new Date(row.batch_date).toLocaleDateString(),
    },
    {
      id: 'status',
      label: 'Status',
      renderCell: (row) => (
        <Box display="flex" alignItems="center" style={{ gap: 6 }}>
          {getStatusIcon(row.status)}
          <StatusChip size="small" label={row.status} color={getStatusChipColor(row.status)} />
        </Box>
      ),
    },
    { id: 'total_calls', label: 'Total' },
    { id: 'processed_calls', label: 'Processed' },
    {
      id: 'passed_calls',
      label: 'Passed',
      renderCell: (row) => (
        <span style={{ color: 'var(--color-success)', fontWeight: 600 }}>{row.passed_calls}</span>
      ),
    },
    {
      id: 'failed_calls',
      label: 'Failed',
      renderCell: (row) => (
        <span style={{ color: 'var(--color-error)', fontWeight: 600 }}>{row.failed_calls}</span>
      ),
    },
    {
      id: 'error_calls',
      label: 'Errors',
      renderCell: (row) => (
        <span style={{ color: 'var(--color-warning)', fontWeight: 600 }}>{row.error_calls}</span>
      ),
    },
    {
      id: 'duration',
      label: 'Duration',
      renderCell: (row) => formatDuration(row.started_at, row.completed_at),
    },
    {
      id: 'actions',
      label: 'Actions',
      renderCell: (row) => (
        <Box display="flex" alignItems="center">
          {(row.status === 'running' || row.status === 'pending') && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                cancelBatch(row.id)
              }}
              title="Cancel Batch"
              style={{ color: 'var(--color-error)' }}
            >
              <StopIcon fontSize="small" />
            </IconButton>
          )}
          {row.report_s3_key && (
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation()
                downloadReport(row.id)
              }}
              title="Download Report"
            >
              <DownloadIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation()
              fetchBatchDetails(row.id)
            }}
            title="View Details"
          >
            {expandedBatch === row.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      ),
    },
  ]

  const batchDetailsColumns = [
    { id: 'call_id', label: 'Call ID' },
    { id: 'campaign_name', label: 'Campaign' },
    { id: 'agent_name', label: 'Agent' },
    {
      id: 'status',
      label: 'Status',
      renderCell: (row) => (
        <StatusChip
          size="small"
          label={row.status}
          color={row.status === 'completed' ? 'success' : 'neutral'}
        />
      ),
    },
    {
      id: 'qa_grade',
      label: 'Grade',
      renderCell: (row) =>
        row.qa_grade ? (
          <StatusChip
            size="small"
            label={row.qa_grade}
            color={row.qa_grade === 'PASS' ? 'success' : 'error'}
          />
        ) : null,
    },
    {
      id: 'overall_score',
      label: 'Score',
      renderCell: (row) => (
        <Typography variant="body2" style={{ fontWeight: 500 }}>
          {row.overall_score !== null ? `${row.overall_score}/${row.total_applicable}` : '-'}
        </Typography>
      ),
    },
    {
      id: 'error_message',
      label: 'Error',
      renderCell: (row) => (
        <Typography
          variant="body2"
          style={{ color: 'var(--color-error)', fontSize: 'var(--font-size-sm)' }}
        >
          {row.error_message || '-'}
        </Typography>
      ),
    },
  ]

  return (
    <Box className={styles.root}>
      {/* Status Card */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Box>
              <Typography variant="h6" className={styles.cardTitle} gutterBottom>
                Configuration Status
              </Typography>
              <Box display="flex" alignItems="center" style={{ gap: 12 }}>
                <StatusChip
                  label={status?.enabled ? 'Enabled' : 'Disabled'}
                  color={status?.enabled ? 'success' : 'neutral'}
                />
                <Typography variant="body2" color="textSecondary">
                  Batch Size: <strong>{status?.batchSize || 0}</strong> calls
                </Typography>
              </Box>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8 }}>
                {status?.message}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              size="small"
              onClick={refetchStatus}
              startIcon={<RefreshIcon />}
              className={`${styles.actionButton} ${styles.outlinedButton}`}
            >
              Refresh
            </Button>
          </Box>
        </CardContent>
      </Card>

      {/* Batch Selection Config */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Box>
              <Typography variant="h6" className={styles.cardTitle}>
                Batch Selection Configuration
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Configure which campaigns are prioritized and the max call duration for batch
                processing.
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" style={{ gap: 8 }}>
              {configSaved && <StatusChip size="small" label="Saved" color="success" />}
              <Tooltip title={!configHasChanges && !savingConfig ? 'No changes to save' : ''} arrow>
                <span>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleSaveConfig}
                    disabled={!configHasChanges || savingConfig}
                    startIcon={savingConfig ? <CircularProgress size={16} /> : <SaveIcon />}
                    className={`${styles.actionButton} ${styles.primaryButton}`}
                  >
                    Save
                  </Button>
                </span>
              </Tooltip>
            </Box>
          </Box>

          {configLoading ? (
            <LoadingSpinner size={24} />
          ) : (
            <>
              <Box mb={3}>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  Priority Campaigns ({priorityCampaigns.length})
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Calls from these campaigns will be selected first in each batch run.
                </Typography>
                {allCampaigns.length > 0 &&
                  priorityCampaigns.some((c) => !allCampaigns.includes(c)) && (
                    <Box
                      mb={2}
                      p={1.5}
                      style={{
                        background: 'var(--color-bg-subtle)',
                        border: '1px solid var(--color-warning)',
                        borderRadius: 8,
                      }}
                    >
                      <Typography
                        variant="body2"
                        style={{ fontWeight: 600, color: 'var(--color-warning)' }}
                      >
                        Unmatched priority campaigns (ignored by batch selection)
                      </Typography>
                      <Typography
                        variant="caption"
                        style={{ color: 'var(--color-text-secondary)' }}
                      >
                        These names match no current campaign, so their calls are never prioritized.
                        Remove them here, or re-add the correct campaign below.
                      </Typography>
                      <Box mt={1}>
                        {priorityCampaigns
                          .filter((c) => !allCampaigns.includes(c))
                          .map((name) => (
                            <Chip
                              key={name}
                              label={name}
                              size="small"
                              onDelete={() => {
                                setPriorityCampaigns(priorityCampaigns.filter((x) => x !== name))
                                setConfigHasChanges(true)
                              }}
                              style={{
                                margin: 2,
                                backgroundColor: 'var(--color-bg-light)',
                                color: 'var(--color-warning)',
                              }}
                            />
                          ))}
                      </Box>
                    </Box>
                  )}
                <Autocomplete
                  multiple
                  options={allCampaigns}
                  value={priorityCampaigns.filter((c) => allCampaigns.includes(c))}
                  onChange={(_, newValue) => {
                    setPriorityCampaigns(newValue)
                    setConfigHasChanges(true)
                  }}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => (
                      <Chip
                        key={option}
                        label={option}
                        size="small"
                        {...getTagProps({ index })}
                        style={{
                          margin: 2,
                          backgroundColor: 'var(--color-bg-light)',
                          color: 'var(--color-info)',
                        }}
                      />
                    ))
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      placeholder={
                        priorityCampaigns.length === 0 ? 'Search and add campaigns...' : ''
                      }
                      InputProps={{
                        ...params.InputProps,
                        style: { borderRadius: 12 },
                      }}
                    />
                  )}
                  filterSelectedOptions
                  disableCloseOnSelect
                />
              </Box>

              <Box>
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  Max Call Duration
                </Typography>
                <Typography variant="body2" color="textSecondary" style={{ marginBottom: 8 }}>
                  Skip calls longer than this duration to control processing costs.
                </Typography>
                <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                  <TextField
                    type="number"
                    variant="outlined"
                    size="small"
                    value={Math.floor(maxHandleTime / 60)}
                    onChange={(e) => {
                      const minutes = parseInt(e.target.value, 10) || 1
                      setMaxHandleTime(Math.max(1, minutes) * 60)
                      setConfigHasChanges(true)
                    }}
                    style={{ width: 100 }}
                    inputProps={{ min: 1, max: 60 }}
                    InputProps={{ style: { borderRadius: 12 } }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    minutes ({maxHandleTime} seconds)
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </CardContent>
      </Card>

      {/* Dry Run Section */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Box>
              <Typography variant="h6" className={styles.cardTitle}>
                Dry Run Preview
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Preview which calls would be selected for processing without actually running the
                batch.
              </Typography>
            </Box>
          </Box>

          <Box className={styles.formRow}>
            <TextField
              label="Target Date (optional)"
              type="date"
              value={runDate}
              onChange={(e) => setRunDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              helperText="Leave blank for previous day"
              InputProps={{ style: { borderRadius: 12 } }}
            />
            <TextField
              label="Limit (optional)"
              type="number"
              value={runLimit}
              onChange={(e) => setRunLimit(e.target.value)}
              size="small"
              style={{ width: 120 }}
              helperText="Override batch size"
              InputProps={{ style: { borderRadius: 12 } }}
            />
            <Button
              variant="contained"
              onClick={runDryRun}
              disabled={loading}
              startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <PreviewIcon />}
              className={`${styles.actionButton} ${styles.primaryButton}`}
            >
              Run Dry Run
            </Button>
          </Box>

          {dryRunResult && (
            <Box className={styles.dryRunResults}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
              >
                Dry Run Results
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      Target Date
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {dryRunResult.targetDate}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      Total Eligible
                    </Typography>
                    <Typography variant="body1" style={{ fontWeight: 600 }}>
                      {dryRunResult.totalEligible}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      Would Process
                    </Typography>
                    <Typography
                      variant="body1"
                      style={{ fontWeight: 700, color: 'var(--color-text-primary)' }}
                    >
                      {dryRunResult.wouldProcess}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} md={3}>
                  <Box className={styles.statItem}>
                    <Typography variant="body2" color="textSecondary">
                      Feature Status
                    </Typography>
                    <StatusChip
                      size="small"
                      label={dryRunResult.enabled ? 'Enabled' : 'Disabled'}
                      color={dryRunResult.enabled ? 'success' : 'neutral'}
                    />
                  </Box>
                </Grid>
              </Grid>

              {dryRunResult.calls?.length > 0 && (
                <Box mt={2}>
                  <Typography
                    variant="subtitle2"
                    gutterBottom
                    style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                  >
                    Selected Calls ({dryRunResult.calls.length})
                  </Typography>
                  <BaseTable
                    columns={dryRunColumns}
                    data={dryRunResult.calls}
                    containerProps={{
                      style: { maxHeight: 300, overflowY: 'auto', borderRadius: 12 },
                    }}
                    tableProps={{ size: 'small' }}
                  />
                </Box>
              )}
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Manual Batch Run */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Box>
              <Typography variant="h6" className={styles.cardTitle}>
                Manual Batch Run
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Start a batch QA run to process calls through the AI pipeline.
              </Typography>
            </Box>
          </Box>
          <Box display="flex" style={{ gap: 12 }} alignItems="center">
            <Button
              variant="contained"
              onClick={() => setRunDialog(true)}
              disabled={runningBatch}
              startIcon={
                runningBatch ? <CircularProgress size={20} color="inherit" /> : <PlayIcon />
              }
              className={`${styles.actionButton} ${styles.primaryButton}`}
            >
              {runningBatch ? 'Running...' : 'Start Batch Run'}
            </Button>
            {runningBatch && streamSessionId && (
              <Button
                variant="outlined"
                onClick={stopBatchRun}
                startIcon={<StopIcon />}
                className={styles.actionButton}
                style={{ borderColor: 'var(--color-error)', color: 'var(--color-error)' }}
              >
                Stop
              </Button>
            )}
          </Box>

          {/* Streaming Progress */}
          {runningBatch && streamProgress && (
            <Box
              style={{
                marginTop: 16,
                padding: 16,
                borderRadius: 12,
                border: '1px solid var(--color-border)',
                backgroundColor: 'var(--color-bg-light)',
              }}
            >
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography
                  variant="subtitle2"
                  style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  Processing: {streamProgress.current}/{streamProgress.total}
                </Typography>
                <Typography
                  variant="body2"
                  style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  {streamProgress.progress}%
                </Typography>
              </Box>
              <Box
                style={{
                  width: '100%',
                  backgroundColor: 'var(--color-border)',
                  borderRadius: 6,
                  height: 8,
                }}
              >
                <Box
                  style={{
                    width: `${streamProgress.progress}%`,
                    backgroundColor: 'var(--color-deep-blue)',
                    borderRadius: 6,
                    height: 8,
                    transition: 'width 0.3s ease',
                  }}
                />
              </Box>
              {streamProgress.stats && (
                <Box display="flex" style={{ gap: 8 }} mt={1.5}>
                  <StatusChip
                    size="small"
                    label={`Passed: ${streamProgress.stats.passedCount}`}
                    color="success"
                    variant="soft"
                  />
                  <StatusChip
                    size="small"
                    label={`Failed: ${streamProgress.stats.failedCount}`}
                    color="error"
                    variant="soft"
                  />
                  <StatusChip
                    size="small"
                    label={`Errors: ${streamProgress.stats.errorCount}`}
                    color="warning"
                    variant="soft"
                  />
                </Box>
              )}
            </Box>
          )}

          {/* Stream Logs */}
          {streamLogs.length > 0 && (
            <Box
              style={{
                marginTop: 16,
                padding: 16,
                maxHeight: 200,
                overflowY: 'auto',
                backgroundColor: 'var(--color-bg-tooltip, #1e1e1e)',
                fontFamily: 'monospace',
                fontSize: 'var(--font-size-sm)',
                borderRadius: 12,
                border: '1px solid var(--color-border)',
              }}
            >
              {streamLogs.map((log, i) => (
                <Box key={i} style={{ marginBottom: 4 }}>
                  <span style={{ color: 'var(--color-text-secondary)', marginRight: 8 }}>
                    {log.timestamp.toLocaleTimeString()}
                  </span>
                  <span
                    style={{
                      color:
                        log.type === 'error'
                          ? 'var(--color-error)'
                          : log.type === 'success'
                            ? 'var(--color-success)'
                            : log.type === 'warning'
                              ? 'var(--color-warning)'
                              : 'var(--color-text-light)',
                    }}
                  >
                    {log.message}
                  </span>
                </Box>
              ))}
            </Box>
          )}

          {batchResult && !runningBatch && (
            <Alert
              severity={batchResult.success ? 'success' : 'error'}
              style={{ marginTop: 16, borderRadius: 12, fontWeight: 500 }}
            >
              {batchResult.success
                ? `Batch #${batchResult.batchRunId} completed: ${batchResult.processed_calls || batchResult.totalCalls} calls processed`
                : `Error: ${batchResult.message}`}
            </Alert>
          )}
        </CardContent>
      </Card>

      {/* Batch History */}
      <Card className={styles.card}>
        <CardContent className={sharedStyles.cardContent}>
          <Box className={styles.cardHeader}>
            <Typography variant="h6" className={styles.cardTitle}>
              Batch Run History
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={refetchHistory}
              startIcon={<RefreshIcon />}
              className={`${styles.actionButton} ${styles.outlinedButton}`}
            >
              Refresh
            </Button>
          </Box>

          <BaseTable
            columns={batchHistoryColumns}
            data={history}
            onRowClick={(row) => fetchBatchDetails(row.id)}
            containerProps={{ style: { maxHeight: 400 } }}
            tableProps={{ size: 'small' }}
            emptyState={
              <Box className={styles.emptyState}>
                <HistoryIcon className={styles.emptyIcon} />
                <Typography variant="h6" gutterBottom>
                  No Batch Runs Yet
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Start your first batch run to see history here
                </Typography>
              </Box>
            }
          />

          {/* Expanded Batch Details */}
          {expandedBatch && batchDetails[expandedBatch] && (
            <Collapse in>
              <Box
                p={2}
                mt={2}
                style={{
                  backgroundColor: 'var(--color-bg-light)',
                  borderRadius: 12,
                  border: '1px solid var(--color-border)',
                }}
              >
                <Typography
                  variant="subtitle2"
                  gutterBottom
                  style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}
                >
                  Call Results ({batchDetails[expandedBatch].length})
                </Typography>
                <BaseTable
                  columns={batchDetailsColumns}
                  data={batchDetails[expandedBatch]}
                  tableProps={{ size: 'small' }}
                />
              </Box>
            </Collapse>
          )}
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog
        open={runDialog}
        onClose={() => setRunDialog(false)}
        PaperProps={{ style: { borderRadius: 16 } }}
      >
        <DialogTitle style={{ borderBottom: '1px solid var(--color-border)' }}>
          <Typography variant="h6" style={{ fontWeight: 600, color: 'var(--color-text-primary)' }}>
            Start Batch Run
          </Typography>
        </DialogTitle>
        <DialogContent style={{ padding: 24 }}>
          <Typography variant="body2" style={{ marginBottom: 16 }}>
            This will start processing calls with the AI pipeline (transcript, summary, QA
            evaluation). Each call takes approximately 30-60 seconds to process.
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
            <TextField
              label="Target Date"
              type="date"
              value={runDate}
              onChange={(e) => setRunDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              variant="outlined"
              helperText="Leave blank for previous day"
              InputProps={{ style: { borderRadius: 12 } }}
            />
            <TextField
              label="Limit"
              type="number"
              value={runLimit}
              onChange={(e) => setRunLimit(e.target.value)}
              fullWidth
              variant="outlined"
              helperText={`Leave blank to use configured batch size (${status?.batchSize || 0})`}
              InputProps={{ style: { borderRadius: 12 } }}
            />
          </Box>
        </DialogContent>
        <DialogActions style={{ padding: '16px 24px', borderTop: '1px solid var(--color-border)' }}>
          <Button
            onClick={() => setRunDialog(false)}
            style={{ color: 'var(--color-text-secondary)' }}
            className={styles.actionButton}
          >
            Cancel
          </Button>
          <Button
            onClick={() => startBatchRun()}
            variant="contained"
            className={`${styles.actionButton} ${styles.primaryButton}`}
          >
            Start Batch
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
